import { useEffect, useState } from 'react';
import { Button, Label, Select } from 'flowbite-react';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { useAssignTeacherSubject, useSchoolGradeSubjects, useSchoolGrades, useTeacherAssignmentsList, useTeachersList, useUnassignTeacherSubject } from '../../react-query/queries';

const Teachers = () => {
  const { token, role } = useAuth();
  const isAllowed = !!token && (role === 'superuser' || role === 'school' || role === 'college_university');

  const { data: teachersData } = useTeachersList(token);
  const [teacherId, setTeacherId] = useState<number | null>(null);
  const { data: assignmentsData, refetch: refetchAssign } = useTeacherAssignmentsList(token, teacherId);

  const { data: gradesData } = useSchoolGrades(token);
  const [gradeId, setGradeId] = useState<number | null>(null);
  const { data: subjectsData } = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const [subjectId, setSubjectId] = useState<number | null>(null);

  const assignMut = useAssignTeacherSubject();
  const unassignMut = useUnassignTeacherSubject();

  useEffect(() => {
    if (teachersData?.teachers?.length && teacherId == null) setTeacherId(teachersData.teachers[0].id);
  }, [teachersData, teacherId]);

  useEffect(() => {
    if (gradesData?.grades?.length && gradeId == null) setGradeId(gradesData.grades[0].id);
  }, [gradesData, gradeId]);

  useEffect(() => {
    if (subjectsData?.subjects?.length && subjectId == null) setSubjectId(subjectsData.subjects[0].id);
  }, [subjectsData, subjectId]);

  if (!isAllowed) return <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">403 - Not authorized</div>;

  const doAssign = async () => {
    if (!teacherId || !subjectId) return;
    await assignMut.mutateAsync({ toBeValidated: token, teacherId, subjectId });
    refetchAssign();
  };

  const doUnassign = async (subId: number) => {
    if (!teacherId) return;
    await unassignMut.mutateAsync({ toBeValidated: token, teacherId, subjectId: subId });
    refetchAssign();
  };

  return (
    <div className="grid grid-cols-12 gap-5">
      {/* Teachers & Assignments */}
      <div className="col-span-12 lg:col-span-5">
        <div className="w-full bg-white px-5 pt-6 pb-4 shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">Teachers</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Select a teacher and manage their subject assignments.</p>
            </div>
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">{teachersData?.teachers?.length ?? 0} total</span>
          </div>

          <div className="mt-3">
            <Label htmlFor="teacherSel" value="Select teacher" />
            <Select id="teacherSel" value={teacherId ?? ''} onChange={(e) => setTeacherId(Number(e.target.value))}>
              {teachersData?.teachers?.map((t: any) => (
                <option key={t.id} value={t.id}>{t.full_name || t.username}</option>
              ))}
            </Select>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Assignments</h4>
              <span className="text-xs text-gray-500">{assignmentsData?.assignments?.length ?? 0} assigned</span>
            </div>
            {assignmentsData?.assignments?.length ? (
              <div className="mt-2 divide-y divide-gray-100 rounded border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
                {assignmentsData.assignments.map((a: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2">
                    <span className="font-medium">{a.grade_name ? `${a.grade_name} - ` : ''}{a.subject_name}</span>
                    <Button size="xs" color="failure" onClick={() => doUnassign(a.subject_id)} isProcessing={unassignMut.isPending}>Unassign</Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 rounded border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">No assignments</div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Subject */}
      <div className="col-span-12 lg:col-span-7">
        <div className="w-full bg-white px-5 pt-6 pb-4 shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold">Assign Subject</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Choose a grade and subject to assign to the selected teacher.</p>
            </div>
          </div>
          <div className="mt-3 rounded border border-gray-100 p-3 dark:border-gray-700">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label htmlFor="gradeSel" value="Grade" />
                <Select id="gradeSel" value={gradeId ?? ''} onChange={(e) => setGradeId(Number(e.target.value))}>
                  {gradesData?.grades?.map((g: any) => (
                    <option key={g.id} value={g.id}>{g.grade_name || g.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="subSel" value="Subject" />
                <Select id="subSel" value={subjectId ?? ''} onChange={(e) => setSubjectId(Number(e.target.value))}>
                  {subjectsData?.subjects?.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.subject_name || s.name}</option>
                  ))}
                </Select>
              </div>
              <div className="pb-2">
                <Button onClick={doAssign} isProcessing={assignMut.isPending}>Assign</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Teachers;
